/**
 * Battle boosts (owner 2026-10-21; data/boosts.ts): which run now and until when, buying one for gems, and the
 * GearBonus list a battle started now runs with. Pure: time is always passed in.
 */
import { BalanceData } from "../data/balance";
import type { BoostData } from "../data/boosts";
import { Fix64 } from "../maths/fix64";
import type { GearBonus } from "./gear";
import type { Wallet } from "./wallet";

const SECONDS_PER_MINUTE = 60;
const PERCENT = 100;

export interface BoostsJson { ends: Record<string, number> }

/** Boost id → the Unix second it ends (a past one is simply over). */
export class BoostsState {
  readonly ends = new Map<string, number>();

  toJson(): BoostsJson { return { ends: Object.fromEntries([...this.ends].sort(([a], [b]) => a.localeCompare(b))) }; }

  static fromJson(balance: BalanceData, j: unknown): BoostsState {
    const s = new BoostsState();
    const ends = typeof j === "object" && j !== null ? (j as { ends?: unknown }).ends : undefined;
    if (typeof ends !== "object" || ends === null) return s;
    const known = new Set(balance.commerce.shop.boosts.items.map((b) => b.id));
    for (const [id, at] of Object.entries(ends as Record<string, unknown>)) if (known.has(id) && Number.isInteger(at)) s.ends.set(id, at as number);
    return s;
  }

  /** Seconds `id` has left at `now` (0: not running). */
  left(id: string, now: number): number { return Math.max(0, (this.ends.get(id) ?? 0) - now); }
}

/** The boosts running at `now`, in the data's order. */
export function activeBoosts(balance: BalanceData, state: BoostsState, now: number): BoostData[] {
  return balance.commerce.shop.boosts.items.filter((b) => state.left(b.id, now) > 0);
}

/**
 * Buys one `id` for its gems: it runs `minutes` from now, or that much longer when already running, never past
 * `maxMinutes` ahead (refused, nothing spent, when it would be). Returns true, or why not.
 */
export function buyBoost(balance: BalanceData, state: BoostsState, wallet: Wallet, id: string, now: number): true | string {
  const d = balance.commerce.shop.boosts;
  const b = d.items.find((x) => x.id === id);
  if (!b) return "no such product";
  const from = Math.max(now, state.ends.get(id) ?? 0);
  const ends = from + b.minutes * SECONDS_PER_MINUTE;
  if (ends - now > d.maxMinutes * SECONDS_PER_MINUTE) return "boost full";
  const r = wallet.spend("gems", b.gems, "boost", now, id);
  if (r !== true) return r;
  state.ends.set(id, ends);
  return true;
}

/**
 * The GearBonus list with the running boosts added: damage, fire rate and range on every archetype (the fortress's own
 * tower included, like VIP's tower damage), health on the fortress. No boost returns the list untouched, so a battle
 * without one hashes exactly as before.
 */
export function withBoosts(balance: BalanceData, bonuses: readonly GearBonus[], boosts: readonly BoostData[]): GearBonus[] {
  if (boosts.length === 0) return [...bonuses];
  const f = (stat: BoostData["stat"]) => boosts.filter((b) => b.stat === stat).reduce((n, b) => n.add(Fix64.fromFraction(b.percent, PERCENT)), Fix64.Zero);
  const dmg = f("damage"), rate = f("fireRate"), range = f("range"), hp = f("hp");
  const base = balance.towers.findIndex((t) => t.isBase);
  return bonuses.map((g, i) => ({ ...g, damage: g.damage.add(dmg), fireRate: g.fireRate.add(rate), range: g.range.add(range), hp: i === base ? g.hp.add(hp) : g.hp }));
}
